import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import ConvertiTrialButton from "./ConvertiTrialButton";
import {
  FlaskConical, Building2, Wrench, ClipboardList, AlertTriangle,
  Trash2, KeyRound, Power, CalendarPlus, Pencil, Save,
} from "lucide-react";

const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }) : "—");

/**
 * Iter122bb · Gestione Account di Prova (Trial) — nativa in SuperAdmin.
 * Endpoint: /api/admin/accounts, /api/admin/trial, /api/admin/trial/{id}/limiti,
 *          /api/admin/trial/{id}/estendi, /api/admin/accounts/{id}/{toggle,reset-password,delete}.
 * Guardia: require_izzo_or_super (backend).
 *
 * Props:
 * - richiestaSeed (opzionale): {id, studio_nome, referente, email} da pre-compilare
 */
export default function SuperAdminTrial({ richiestaSeed, onTrialCreated }) {
  const [data, setData] = useState({ trial: [], limiti: {}, sezioni_opzionali: [] });
  const [trial, setTrial] = useState({ name: "", email: "", studio_nome: "", password: "", max_condomini: "", max_fornitori: "", giorni: "", sezioni: [], richiesta_id: null });
  const [busy, setBusy] = useState(false);
  const [limitEdit, setLimitEdit] = useState(null);
  const [limitForm, setLimitForm] = useState({ max_condomini: "", max_fornitori: "", giorni: "", sezioni: [] });

  const load = () => api.get("/admin/accounts").then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail) || "Errore caricamento"));

  useEffect(() => { load(); }, []);

  // Iter122bb · pre-compila il form da una richiesta di prova
  useEffect(() => {
    if (richiestaSeed) {
      setTrial((s) => ({ ...s, name: richiestaSeed.referente || richiestaSeed.studio_nome, email: richiestaSeed.email, studio_nome: richiestaSeed.studio_nome, richiesta_id: richiestaSeed.id, password: "" }));
      document.querySelector('[data-testid="trial-form"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
      toast.info("Dati precompilati: imposta una password e crea l'account di prova.");
    }
  }, [richiestaSeed?.id]);  // eslint-disable-line react-hooks/exhaustive-deps

  const createTrial = async () => {
    if (!trial.name.trim() || !trial.email.trim() || trial.password.length < 6) { toast.error("Compila nome, email e password (min 6)"); return; }
    setBusy(true);
    const payload = {
      name: trial.name, email: trial.email, studio_nome: trial.studio_nome, password: trial.password,
      sezioni: trial.sezioni, richiesta_id: trial.richiesta_id,
      ...(trial.max_condomini ? { max_condomini: parseInt(trial.max_condomini, 10) } : {}),
      ...(trial.max_fornitori ? { max_fornitori: parseInt(trial.max_fornitori, 10) } : {}),
      ...(trial.giorni ? { giorni: parseInt(trial.giorni, 10) } : {}),
    };
    try {
      const { data: t } = await api.post("/admin/trial", payload);
      toast.success(`Account di prova creato (scade il ${fmtDate(t.trial_expires_at)}) · email di benvenuto inviata`);
      setTrial({ name: "", email: "", studio_nome: "", password: "", max_condomini: "", max_fornitori: "", giorni: "", sezioni: [], richiesta_id: null });
      load();
      onTrialCreated && onTrialCreated();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Creazione non riuscita"); }
    setBusy(false);
  };

  const apriLimiti = (a) => {
    setLimitForm({
      max_condomini: a.trial_max_condomini || "", max_fornitori: a.trial_max_fornitori || "",
      giorni: "", sezioni: a.trial_sezioni || [],
    });
    setLimitEdit(a);
  };
  const salvaLimiti = async () => {
    if (!limitEdit) return;
    setBusy(true);
    const payload = {
      sezioni: limitForm.sezioni,
      ...(limitForm.max_condomini ? { max_condomini: parseInt(limitForm.max_condomini, 10) } : {}),
      ...(limitForm.max_fornitori ? { max_fornitori: parseInt(limitForm.max_fornitori, 10) } : {}),
      ...(limitForm.giorni ? { giorni: parseInt(limitForm.giorni, 10) } : {}),
    };
    try { await api.put(`/admin/trial/${limitEdit.id}/limiti`, payload); toast.success("Limiti aggiornati"); setLimitEdit(null); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Salvataggio non riuscito"); }
    setBusy(false);
  };

  const toggle = async (a) => { try { await api.post(`/admin/accounts/${a.id}/toggle`); load(); } catch { toast.error("Operazione non riuscita"); } };
  const resetPw = async (a) => {
    const pw = window.prompt(`Nuova password per ${a.name}:`);
    if (!pw) return;
    if (pw.length < 8) { toast.error("Almeno 8 caratteri"); return; }
    try { await api.post(`/admin/accounts/${a.id}/reset-password`, { password: pw }); toast.success("Password aggiornata"); } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore"); }
  };
  const estendi = async (a) => { try { const { data: r } = await api.post(`/admin/trial/${a.id}/estendi`); toast.success(`Prova estesa al ${fmtDate(r.trial_expires_at)}`); load(); } catch { toast.error("Errore"); } };
  const elimina = async (a) => {
    if (!window.confirm(`Eliminare l'account di prova "${a.name}" e TUTTI i suoi dati di prova?`)) return;
    try { await api.delete(`/admin/accounts/${a.id}`); toast.success("Account eliminato"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore"); }
  };

  return (
    <section data-testid="superadmin-trial-section">
      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="p-5 border-2 h-fit" data-testid="trial-form">
          <div className="flex items-center gap-2 mb-3 font-bold text-slate-700"><FlaskConical className="w-5 h-5" /> Nuovo account di prova</div>
          <div className="mb-3">
            <label className="text-sm font-semibold text-slate-600">Nome referente <span className="text-red-500">*</span></label>
            <Input className="h-11 mt-1" data-testid="trial-name" value={trial.name} onChange={(e) => setTrial({ ...trial, name: e.target.value })} placeholder="Es. Studio Bianchi" />
          </div>
          <div className="mb-3">
            <label className="text-sm font-semibold text-slate-600">Nome studio/azienda (opzionale)</label>
            <Input className="h-11 mt-1" data-testid="trial-studio" value={trial.studio_nome} onChange={(e) => setTrial({ ...trial, studio_nome: e.target.value })} />
          </div>
          <div className="mb-3">
            <label className="text-sm font-semibold text-slate-600">Email <span className="text-red-500">*</span></label>
            <Input className="h-11 mt-1" data-testid="trial-email" type="email" value={trial.email} onChange={(e) => setTrial({ ...trial, email: e.target.value })} placeholder="cliente@studio.it" />
          </div>
          <div className="mb-3">
            <label className="text-sm font-semibold text-slate-600">Password <span className="text-red-500">*</span></label>
            <Input className="h-11 mt-1" data-testid="trial-password" type="text" value={trial.password} onChange={(e) => setTrial({ ...trial, password: e.target.value })} placeholder="min 8 caratteri" />
          </div>
          {trial.richiesta_id && <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1 mb-2" data-testid="trial-da-richiesta">Da richiesta di prova · verrà segnata come "attivata"</div>}
          <div className="grid grid-cols-3 gap-2 mb-2">
            <div><label className="text-xs font-semibold text-slate-500">Max condomìni</label><Input className="h-10 mt-1" type="number" min="1" data-testid="trial-max-condomini" value={trial.max_condomini} onChange={(e) => setTrial({ ...trial, max_condomini: e.target.value })} placeholder={data.limiti?.condomini || 1} /></div>
            <div><label className="text-xs font-semibold text-slate-500">Max fornitori</label><Input className="h-10 mt-1" type="number" min="1" data-testid="trial-max-fornitori" value={trial.max_fornitori} onChange={(e) => setTrial({ ...trial, max_fornitori: e.target.value })} placeholder={data.limiti?.fornitori || 20} /></div>
            <div><label className="text-xs font-semibold text-slate-500">Giorni</label><Input className="h-10 mt-1" type="number" min="1" data-testid="trial-giorni" value={trial.giorni} onChange={(e) => setTrial({ ...trial, giorni: e.target.value })} placeholder={data.limiti?.giorni || 30} /></div>
          </div>
          <div className="mb-3">
            <label className="text-xs font-semibold text-slate-500">Sezioni extra abilitate</label>
            <div className="flex flex-wrap gap-2 mt-1" data-testid="trial-sezioni">
              {(data.sezioni_opzionali || []).map((s) => {
                const on = trial.sezioni.includes(s.key);
                return (
                  <button key={s.key} type="button" data-testid={`trial-sez-${s.key}`}
                    onClick={() => setTrial((t) => ({ ...t, sezioni: on ? t.sezioni.filter((x) => x !== s.key) : [...t.sezioni, s.key] }))}
                    className={`px-2.5 py-1 rounded-full text-xs font-semibold border-2 transition-colors ${on ? "bg-brand text-white border-brand" : "bg-white text-slate-600 border-slate-200"}`}>
                    {on ? "✓ " : ""}{s.label}
                  </button>
                );
              })}
            </div>
          </div>
          <Button data-testid="trial-submit" disabled={busy} onClick={createTrial} className="w-full h-11 bg-brand hover:bg-brand-dark font-bold mt-1"><FlaskConical className="w-5 h-5 mr-2" /> Crea prova ({trial.giorni || data.limiti?.giorni || 30} giorni)</Button>
          <div className="mt-4 text-xs text-slate-500 bg-slate-50 rounded-lg p-3 border">
            <div className="font-semibold text-slate-600 mb-1 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Spazio dati isolato. Limiti e sezioni sono <b>modificabili in ogni momento</b> dalla scheda dell'account.</div>
            <ul className="list-disc list-inside space-y-0.5">
              <li>Base sempre attiva: Cruscotto, Anagrafiche, Calendario, Bacheca, Manutenzioni</li>
              <li>Lascia vuoti i limiti per usare i valori predefiniti ({data.limiti?.condomini || 1} cond. · {data.limiti?.fornitori || 20} forn.)</li>
            </ul>
          </div>
        </Card>
        <div className="lg:col-span-2 space-y-3" data-testid="trial-list">
          {(data.trial || []).length === 0 && <Card className="p-4 text-center text-slate-500">Nessun account di prova attivo.</Card>}
          {(data.trial || []).map((a) => (
            <Card key={a.id} className={`p-4 border-2 ${a.scaduto ? "border-red-200 bg-red-50/40" : ""}`} data-testid={`trial-${a.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-bold text-slate-800">{a.name}
                    {a.studio_nome && <span className="text-slate-400 font-normal"> · {a.studio_nome}</span>}
                    {a.disabilitato && <span className="text-xs text-red-600 font-semibold"> · disabilitato</span>}
                    {a.scaduto && <span className="text-xs text-red-600 font-semibold"> · scaduto</span>}
                  </div>
                  <div className="text-sm text-slate-500">{a.email}</div>
                  <div className="text-sm text-slate-500 mt-1">Scadenza prova: <b className={a.scaduto ? "text-red-600" : "text-slate-700"}>{fmtDate(a.trial_expires_at)}</b></div>
                  <div className="flex flex-wrap gap-3 mt-2 text-xs text-slate-500">
                    <span className="flex items-center gap-1"><Building2 className="w-3.5 h-3.5" /> {a.n_condomini}/{a.trial_max_condomini} cond.</span>
                    <span className="flex items-center gap-1" title="Fornitori con account invitato/attivo (esclusi disabilitati) — anagrafica: illimitata"><Wrench className="w-3.5 h-3.5" /> {a.n_fornitori}/{a.trial_max_fornitori} forn.</span>
                    <span className="flex items-center gap-1"><ClipboardList className="w-3.5 h-3.5" /> {a.n_manutenzioni} segn.</span>
                  </div>
                  {a.trial_sezioni?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5" data-testid={`trial-sezioni-${a.id}`}>
                      {a.trial_sezioni.map((k) => {
                        const lab = (data.sezioni_opzionali || []).find((s) => s.key === k)?.label || k;
                        return <span key={k} className="px-2 py-0.5 rounded-full bg-brand/10 text-brand text-[11px] font-semibold">{lab}</span>;
                      })}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => apriLimiti(a)} data-testid={`trial-limiti-${a.id}`} title="Modifica limiti e sezioni"><Pencil className="w-4 h-4" /></Button>
                  <Button variant="outline" size="sm" onClick={() => estendi(a)} data-testid={`trial-extend-${a.id}`} title="Estendi 30 giorni"><CalendarPlus className="w-4 h-4" /></Button>
                  <ConvertiTrialButton trial={a} onDone={load} />
                  <Button variant="outline" size="sm" onClick={() => resetPw(a)} data-testid={`trial-reset-${a.id}`}><KeyRound className="w-4 h-4" /></Button>
                  <Button variant="outline" size="sm" onClick={() => toggle(a)} data-testid={`trial-toggle-${a.id}`} className={a.disabilitato ? "text-green-700" : "text-amber-700"}><Power className="w-4 h-4" /></Button>
                  <Button variant="outline" size="sm" onClick={() => elimina(a)} data-testid={`trial-del-${a.id}`} className="text-red-600"><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>

      <Dialog open={!!limitEdit} onOpenChange={(o) => !o && setLimitEdit(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="text-xl flex items-center gap-2"><Pencil className="w-5 h-5 text-brand" /> Limiti prova — {limitEdit?.name}</DialogTitle></DialogHeader>
          {limitEdit && (
            <div className="space-y-3" data-testid="limiti-dialog">
              <div className="grid grid-cols-3 gap-2">
                <div><label className="text-xs font-semibold text-slate-500">Max condomìni</label><Input className="h-10 mt-1" type="number" min="1" data-testid="limiti-max-condomini" value={limitForm.max_condomini} onChange={(e) => setLimitForm({ ...limitForm, max_condomini: e.target.value })} /></div>
                <div><label className="text-xs font-semibold text-slate-500">Max fornitori</label><Input className="h-10 mt-1" type="number" min="1" data-testid="limiti-max-fornitori" value={limitForm.max_fornitori} onChange={(e) => setLimitForm({ ...limitForm, max_fornitori: e.target.value })} /></div>
                <div><label className="text-xs font-semibold text-slate-500">Giorni (da oggi)</label><Input className="h-10 mt-1" type="number" min="1" data-testid="limiti-giorni" placeholder="invariato" value={limitForm.giorni} onChange={(e) => setLimitForm({ ...limitForm, giorni: e.target.value })} /></div>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500">Sezioni extra abilitate</label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {(data.sezioni_opzionali || []).map((s) => {
                    const on = limitForm.sezioni.includes(s.key);
                    return (
                      <button key={s.key} type="button" data-testid={`limiti-sez-${s.key}`}
                        onClick={() => setLimitForm((f) => ({ ...f, sezioni: on ? f.sezioni.filter((x) => x !== s.key) : [...f.sezioni, s.key] }))}
                        className={`px-2.5 py-1 rounded-full text-xs font-semibold border-2 transition-colors ${on ? "bg-brand text-white border-brand" : "bg-white text-slate-600 border-slate-200"}`}>
                        {on ? "✓ " : ""}{s.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <p className="text-xs text-slate-400">Indicare i Giorni reimposta la scadenza a "oggi + N giorni" e riattiva l'account. Lascia vuoto per non cambiarla.</p>
              <Button data-testid="limiti-salva" disabled={busy} onClick={salvaLimiti} className="w-full h-11 bg-brand hover:bg-brand-dark font-bold"><Save className="w-4 h-4 mr-2" /> Salva limiti</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
